import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { formatCurrency, formatDate, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
    Search,
    Phone,
    ShoppingCart,
    UserX,
    MessageSquare,
    CheckCircle,
    XCircle,
    RefreshCw,
    Filter,
    ExternalLink,
    Upload,
    Loader2
} from 'lucide-react';
import api, { abandonedAPI } from '../lib/api';

const statusOptions = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'nouveau', label: 'Nouveau' },
    { value: 'contacte', label: 'Contacté' },
    { value: 'converti', label: 'Converti' },
    { value: 'perdu', label: 'Perdu' },
];

const getStatusColor = (status) => {
    const colors = {
        nouveau: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200',
        contacte: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200',
        converti: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200',
        perdu: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
};

const getStatusLabel = (status) => {
    const labels = {
        nouveau: 'Nouveau',
        contacte: 'Contacté',
        converti: 'Converti',
        perdu: 'Perdu',
    };
    return labels[status] || status;
};

export default function Abandoned() {
    const [orders, setOrders] = useState([]);
    const [stats, setStats] = useState({ total: 0, nouveau: 0, contacte: 0, converti: 0, perdu: 0 });
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [notes, setNotes] = useState('');
    const [newStatus, setNewStatus] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            const params = {};
            if (statusFilter !== 'all') params.status = statusFilter;
            if (search) params.search = search;
            
            const [ordersRes, statsRes] = await Promise.all([
                api.get('/abandoned-orders', { params }),
                api.get('/abandoned-orders/stats')
            ]);
            
            setOrders(ordersRes.data);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Error fetching abandoned orders:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, search]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenDialog = (order) => {
        setSelectedOrder(order);
        setNotes(order.notes || '');
        setNewStatus(order.status);
        setDialogOpen(true);
    };

    const handleUpdateOrder = async () => {
        if (!selectedOrder) return;
        setSubmitting(true);
        
        try {
            await api.put(`/abandoned-orders/${selectedOrder.id}`, {
                status: newStatus,
                notes: notes
            });
            toast.success('Commande mise à jour');
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        } finally {
            setSubmitting(false);
        }
    };

    const handleConvert = async (order) => {
        try {
            const response = await api.post(`/abandoned-orders/${order.id}/convert`);
            toast.success(`Commande créée: ${response.data.n_commande}`);
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la conversion');
        }
    };

    const handleImportFile = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setImporting(true);
        setImportResult(null);

        try {
            const response = await abandonedAPI.importFromFile(file);
            setImportResult(response.data);
            toast.success(`Import réussi ! ${response.data.imported} nouvelles, ${response.data.skipped} ignorées (doublons)`);
            fetchData();
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de l\'import';
            toast.error(message);
            setImportResult({ error: message });
        } finally {
            setImporting(false);
            event.target.value = ''; // Reset file input
        }
    };

    const statCards = [
        { label: 'Total', value: stats.total, icon: UserX, color: 'text-gray-600', bgColor: 'bg-gray-100' },
        { label: 'Nouveaux', value: stats.nouveau, icon: MessageSquare, color: 'text-blue-600', bgColor: 'bg-blue-100' },
        { label: 'Contactés', value: stats.contacte, icon: Phone, color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
        { label: 'Convertis', value: stats.converti, icon: CheckCircle, color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
        { label: 'Perdus', value: stats.perdu, icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100' },
    ];

    return (
        <Layout>
            <div className="space-y-6" data-testid="abandoned-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-semibold tracking-tight">Commandes Abandonnées</h1>
                        <p className="text-muted-foreground mt-1">
                            Récupérez vos clients potentiels
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={fetchData}
                            disabled={loading}
                            data-testid="refresh-btn"
                        >
                            <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
                            Actualiser
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => document.getElementById('abandoned-file-import').click()}
                            disabled={importing}
                            className="border-green-500 text-green-600 hover:bg-green-50"
                        >
                            {importing ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Upload className="w-4 h-4 mr-2" />
                            )}
                            Import CSV
                        </Button>
                        <input
                            id="abandoned-file-import"
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            onChange={handleImportFile}
                            style={{ display: 'none' }}
                        />
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {statCards.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <Card key={index} className="card-luxury">
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className={cn('w-10 h-10 rounded-[2px] flex items-center justify-center', stat.bgColor)}>
                                            <Icon className={cn('w-5 h-5', stat.color)} />
                                        </div>
                                        <div>
                                            <p className="text-2xl font-semibold">{stat.value}</p>
                                            <p className="text-xs text-muted-foreground">{stat.label}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* Filters */}
                <Card className="card-luxury">
                    <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Rechercher par nom, produit, téléphone..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="pl-10 input-luxury"
                                        data-testid="search-input"
                                    />
                                </div>
                            </div>
                            <div className="w-full md:w-48">
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="input-luxury" data-testid="status-filter">
                                        <Filter className="w-4 h-4 mr-2" />
                                        <SelectValue placeholder="Statut" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {statusOptions.map((option) => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Table */}
                <Card className="card-luxury">
                    <CardContent className="p-0">
                        <div className="table-container">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="table-header">Date</TableHead>
                                        <TableHead className="table-header">Client</TableHead>
                                        <TableHead className="table-header">Produit</TableHead>
                                        <TableHead className="table-header">Prix</TableHead>
                                        <TableHead className="table-header">Statut</TableHead>
                                        <TableHead className="table-header">Notes</TableHead>
                                        <TableHead className="table-header">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center py-12">
                                                <div className="flex justify-center">
                                                    <div className="spinner" />
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : orders.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                                                Aucune commande abandonnée
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        orders.map((order) => (
                                            <TableRow key={order.id} className="table-row">
                                                <TableCell>
                                                    <span className="text-sm text-muted-foreground">
                                                        {formatDate(order.date)}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <div>
                                                        <p className="font-medium">{order.full_name}</p>
                                                        <a 
                                                            href={`tel:${order.phone}`}
                                                            className="text-sm text-primary hover:underline flex items-center gap-1"
                                                        >
                                                            <Phone className="w-3 h-3" />
                                                            {order.phone}
                                                        </a>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <span className="truncate max-w-[180px]">{order.produit}</span>
                                                        {order.produit_url && (
                                                            <a
                                                                href={order.produit_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-primary"
                                                            >
                                                                <ExternalLink className="w-4 h-4" />
                                                            </a>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="font-semibold">
                                                    {formatCurrency(order.prix)}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={cn('status-badge', getStatusColor(order.status))}>
                                                        {getStatusLabel(order.status)}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-sm text-muted-foreground truncate max-w-[150px] block">
                                                        {order.notes || '-'}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleOpenDialog(order)}
                                                            data-testid={`edit-${order.id}`}
                                                        >
                                                            <MessageSquare className="w-4 h-4" />
                                                        </Button>
                                                        {order.status !== 'converti' && (
                                                            <Button
                                                                size="sm"
                                                                className="btn-luxury"
                                                                onClick={() => handleConvert(order)}
                                                                data-testid={`convert-${order.id}`}
                                                            >
                                                                <ShoppingCart className="w-4 h-4 mr-1" />
                                                                Convertir
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>

                {/* Edit Dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Suivi Client</DialogTitle>
                        </DialogHeader>
                        {selectedOrder && (
                            <div className="space-y-4">
                                <div className="p-4 rounded-[2px] bg-muted/30">
                                    <p className="font-medium">{selectedOrder.full_name}</p>
                                    <p className="text-sm text-muted-foreground">{selectedOrder.phone}</p>
                                    <p className="text-sm mt-2">{selectedOrder.produit}</p>
                                    <p className="font-semibold text-primary mt-1">{formatCurrency(selectedOrder.prix)}</p>
                                </div>
                                
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Statut</label>
                                    <Select value={newStatus} onValueChange={setNewStatus}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="nouveau">Nouveau</SelectItem>
                                            <SelectItem value="contacte">Contacté</SelectItem>
                                            <SelectItem value="converti">Converti</SelectItem>
                                            <SelectItem value="perdu">Perdu</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Notes</label>
                                    <Textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Ajouter des notes sur le contact..."
                                        rows={3}
                                    />
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button 
                                className="btn-luxury" 
                                onClick={handleUpdateOrder}
                                disabled={submitting}
                            >
                                {submitting ? 'Enregistrement...' : 'Enregistrer'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
